import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../App';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import SimpleRichTextEditor from './ui/rich-text-editor';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Briefcase, MapPin, Clock, IndianRupee, Tag, X, Eye, ArrowLeft, Upload, Image } from 'lucide-react';
import { toast } from 'sonner';
import SEOHead from './SEOHead';

const API = process.env.REACT_APP_BACKEND_URL;

const PostJob = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editJobId = searchParams.get('edit');
  const logoInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: '',
    company_name: user?.full_name || '',
    company_logo: '',
    location: '',
    location_type: 'onsite',
    job_type: 'full-time',
    description: '',
    requirements: '',
    experience_min: 0,
    experience_max: 0,
    salary_min: '',
    salary_max: '',
    salary_currency: 'INR',
    tags: [],
    status: 'draft'
  });
  
  const [tagInput, setTagInput] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingJob, setLoadingJob] = useState(!!editJobId);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    if (editJobId) {
      fetchJobForEdit();
    }
  }, [editJobId]);

  const fetchJobForEdit = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/jobs/${editJobId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success && data.job) {
        setFormData({
          ...data.job,
          salary_min: data.job.salary_min || '',
          salary_max: data.job.salary_max || ''
        });
      }
    } catch (error) {
      toast.error('Failed to load job');
    } finally {
      setLoadingJob(false);
    }
  };

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only image files (JPEG, PNG, GIF, WebP) are allowed');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo file must be less than 2MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const token = localStorage.getItem('token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await fetch(`${API}/api/jobs/upload-logo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });

      const data = await response.json();
      if (data.success) {
        setFormData(prev => ({ ...prev, company_logo: data.logo_url }));
        toast.success('Logo uploaded successfully');
      } else {
        toast.error(data.detail || 'Failed to upload logo');
      }
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tagInput.trim()] }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tagToRemove) }));
  };

  const handleSubmit = async (status) => {
    if (!formData.title || !formData.company_name || !formData.location || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        salary_min: formData.salary_min ? parseInt(formData.salary_min) : null,
        salary_max: formData.salary_max ? parseInt(formData.salary_max) : null,
        status
      };

      const url = editJobId ? `${API}/api/jobs/${editJobId}` : `${API}/api/jobs`;
      const method = editJobId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.success) {
        toast.success(status === 'active' ? 'Job published successfully!' : 'Job saved as draft');
        navigate('/employer-dashboard');
      } else {
        toast.error(data.detail || 'Failed to save job');
      }
    } catch (error) {
      toast.error('Failed to save job');
    } finally {
      setLoading(false);
    }
  };

  if (loadingJob) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30 flex items-center justify-center">
        <p>Loading job...</p>
      </div>
    );
  }

  // Preview Mode
  if (showPreview) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-6 flex items-center justify-between">
            <Button variant="ghost" onClick={() => setShowPreview(false)}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Edit
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => handleSubmit('draft')} disabled={loading}>
                Save Draft
              </Button>
              <Button onClick={() => handleSubmit('active')} disabled={loading} className="bg-orange-500 hover:bg-orange-600">
                Publish Live
              </Button>
            </div>
          </div>

          <Card>
            <CardContent className="p-8">
              <div className="flex items-start gap-4 mb-6">
                {formData.company_logo ? (
                  <img 
                    src={formData.company_logo.startsWith('data:') ? formData.company_logo : `${API}${formData.company_logo}`} 
                    alt="Company logo" 
                    className="w-16 h-16 object-cover rounded-lg" loading="lazy" decoding="async"/>
                ) : (
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-100 to-amber-100 rounded-lg flex items-center justify-center">
                    <Briefcase className="h-8 w-8 text-orange-600" />
                  </div>
                )}
                <div className="flex-1">
                  <h1 className="text-2xl font-bold text-gray-900">{formData.title || 'Job Title'}</h1>
                  <p className="text-lg text-gray-600">{formData.company_name}</p>
                  <div className="flex flex-wrap gap-3 mt-2 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <MapPin className="h-4 w-4" /> {formData.location || 'Location'}
                    </span>
                    <Badge variant="secondary">{formData.location_type}</Badge>
                    <Badge variant="secondary">{formData.job_type}</Badge>
                  </div>
                </div>
              </div>

              {(formData.salary_min || formData.salary_max) && (
                <div className="flex items-center gap-2 mb-6 text-lg">
                  <IndianRupee className="h-5 w-5 text-green-600" />
                  <span className="font-semibold text-green-700">
                    {formData.salary_min && formData.salary_max 
                      ? `${formData.salary_min.toLocaleString()} - ${formData.salary_max.toLocaleString()}`
                      : formData.salary_min 
                        ? `From ${formData.salary_min.toLocaleString()}`
                        : `Up to ${formData.salary_max.toLocaleString()}`
                    } {formData.salary_currency}/year
                  </span>
                </div>
              )}

              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Experience Required</h2>
                <p className="text-gray-600">
                  {formData.experience_min === 0 && formData.experience_max === 0
                    ? 'Freshers welcome'
                    : `${formData.experience_min} - ${formData.experience_max} years`}
                </p>
              </div>

              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Job Description</h2>
                <div 
                  className="rich-text-content text-gray-600"
                  dangerouslySetInnerHTML={{ __html: formData.description || '<p>No description provided</p>' }}
                />
              </div>

              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Requirements</h2>
                <div 
                  className="rich-text-content text-gray-600"
                  dangerouslySetInnerHTML={{ __html: formData.requirements || '<p>No requirements specified</p>' }}
                />
              </div>

              {formData.tags.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">Skills & Tags</h2>
                  <div className="flex flex-wrap gap-2">
                    {formData.tags.map((tag, idx) => (
                      <Badge key={idx} variant="secondary" className="bg-orange-50 text-orange-700">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30">
      <SEOHead
        title={editJobId ? 'Edit Job' : 'Post a New Job'}
        description="Post a new job opening on Nerd Nation Job Board. Reach thousands of qualified students and professionals."
        path="/post-job"
        noIndex
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate('/employer-dashboard')}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-6 w-6 text-orange-500" />
              {editJobId ? 'Edit Job' : 'Post a New Job'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Company Logo */}
            <div>
              <Label>Company Logo</Label>
              <div className="flex items-center gap-4 mt-2">
                {formData.company_logo ? (
                  <div className="relative">
                    <img 
                      src={formData.company_logo.startsWith('data:') ? formData.company_logo : `${API}${formData.company_logo}`} 
                      alt="Company logo" 
                      className="w-20 h-20 object-cover rounded-lg border border-gray-200" loading="lazy" decoding="async"/>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, company_logo: '' }))}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <div 
                    className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-orange-400 transition-colors"
                    onClick={() => logoInputRef.current?.click()}
                  >
                    <Image className="h-8 w-8 text-gray-400" />
                  </div>
                )}
                <div className="flex-1">
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={uploadingLogo}
                    className="mb-1"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {uploadingLogo ? 'Uploading...' : formData.company_logo ? 'Change Logo' : 'Upload Logo'}
                  </Button>
                  <p className="text-xs text-gray-500">JPEG, PNG, GIF, or WebP. Max 2MB.</p>
                </div>
              </div>
            </div>

            {/* Job Basics */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Job Title *</Label>
                <Input
                  placeholder="e.g., Senior Software Engineer"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                />
              </div>
              <div>
                <Label>Company Name *</Label>
                <Input
                  placeholder="Your company name"
                  value={formData.company_name}
                  onChange={(e) => handleChange('company_name', e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Location *</Label>
                <Input
                  placeholder="e.g., Bangalore, India"
                  value={formData.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                />
              </div>
              <div>
                <Label>Location Type</Label>
                <Select value={formData.location_type} onValueChange={(v) => handleChange('location_type', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="onsite">On-site</SelectItem>
                    <SelectItem value="remote">Remote</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Job Type</Label>
                <Select value={formData.job_type} onValueChange={(v) => handleChange('job_type', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full-time">Full-time</SelectItem>
                    <SelectItem value="part-time">Part-time</SelectItem>
                    <SelectItem value="contract">Contract</SelectItem>
                    <SelectItem value="internship">Internship</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Description */}
            <div>
              <Label>Job Description *</Label>
              <SimpleRichTextEditor
                value={formData.description}
                onChange={(v) => handleChange('description', v)}
                placeholder="Describe the role, responsibilities, and what makes this opportunity exciting..."
              />
            </div>

            {/* Experience Range */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Min Experience (Years)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.experience_min}
                  onChange={(e) => handleChange('experience_min', parseInt(e.target.value) || 0)}
                />
              </div>
              <div>
                <Label>Max Experience (Years)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.experience_max}
                  onChange={(e) => handleChange('experience_max', parseInt(e.target.value) || 0)}
                />
              </div>
            </div>

            {/* Requirements */}
            <div>
              <Label>Requirements</Label>
              <SimpleRichTextEditor
                value={formData.requirements}
                onChange={(v) => handleChange('requirements', v)}
                placeholder="List the skills, qualifications, and experience required..."
              />
            </div>

            {/* Salary Range */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Min Salary (Annual)</Label>
                <Input
                  type="number"
                  placeholder="e.g., 500000"
                  value={formData.salary_min}
                  onChange={(e) => handleChange('salary_min', e.target.value)}
                />
              </div>
              <div>
                <Label>Max Salary (Annual)</Label>
                <Input
                  type="number"
                  placeholder="e.g., 1000000"
                  value={formData.salary_max}
                  onChange={(e) => handleChange('salary_max', e.target.value)}
                />
              </div>
              <div>
                <Label>Currency</Label>
                <Select value={formData.salary_currency} onValueChange={(v) => handleChange('salary_currency', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="INR">INR</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Tags */}
            <div>
              <Label>Tags / Skills</Label>
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="e.g., Python, Marketing, Entry Level"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                />
                <Button type="button" variant="outline" onClick={addTag}>
                  <Tag className="h-4 w-4" />
                </Button>
              </div>
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag, idx) => (
                    <Badge key={idx} variant="secondary" className="bg-orange-50 text-orange-700 flex items-center gap-1">
                      {tag}
                      <X className="h-3 w-3 cursor-pointer" onClick={() => removeTag(tag)} />
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowPreview(true)}>
                <Eye className="h-4 w-4 mr-2" /> Preview
              </Button>
              <Button variant="outline" onClick={() => handleSubmit('draft')} disabled={loading}>
                Save Draft
              </Button>
              <Button onClick={() => handleSubmit('active')} disabled={loading} className="bg-orange-500 hover:bg-orange-600">
                Publish Live
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PostJob;
